import GokuDataGrid from '@/components/GokuDataGrid'
import PeriodComparisonFilter, {
    PeriodSelection,
} from '@/components/PeriodComparisonFilter'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { CHART_THEME, METRIC_COLORS } from '@/constants/chartColors'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import AddIcons from '@/icons/AddIcons'
import ChartIcons from '@/icons/ChartIcons'
import GridIcons from '@/icons/GridIcons'
import RatioFormModal from '@/modals/BalanceSheetModals/RatioFormModal'
import dayjs from 'dayjs'
import { Sparkles } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Bar,
    BarChart,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { Button, IconButton, Typogaphy } from 'vnetwork-ui'
import { getBalanceSheetMonthlySummary } from '../../services'
import {
    createDataHash,
    generateCacheKey,
    getCachedAnalysis,
} from '../../services/aiCache'
import {
    analyzeBalanceSheet,
    GeminiReportResponse,
} from '../../services/gemini'
import {
    BalanceSheetMonthlySummaryParams,
    BalanceSheetSummary,
} from '../../types'
import AIAnalysisReport from '../AIAnalysisReport'
import { columns } from './columns'

interface BalanceSheetTabProps {
    filters?: { gte?: string; lte?: string }
}

type BalanceSheetMetricKey = 'total_assets' | 'total_liability' | 'total_equity'

interface MetricConfig {
    key: BalanceSheetMetricKey
    label: string
    color: string
}

const BalanceSheetTab: React.FC<BalanceSheetTabProps> = ({ filters }) => {
    const { t } = useTranslation()
    const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart')
    const [comparisonViewMode, setComparisonViewMode] = useState<
        'chart' | 'table'
    >('chart')
    const [modalOpen, setModalOpen] = useState(false)
    const [modalMode, setModalMode] = useState<'create' | 'edit'>('create')
    const [editData, setEditData] = useState<BalanceSheetSummary | null>(null)

    // AI Analysis states
    const [aiReport, setAiReport] = useState<GeminiReportResponse | null>(null)
    const [aiLoading, setAiLoading] = useState(false)
    const [aiError, setAiError] = useState<string | null>(null)
    const [aiFromCache, setAiFromCache] = useState(false)
    const [showAiReport, setShowAiReport] = useState(false)

    // Metrics configuration
    const metrics: MetricConfig[] = [
        {
            key: 'total_assets',
            label: t('total_assets'),
            color: METRIC_COLORS.total_assets,
        },
        {
            key: 'total_liability',
            label: t('total_liability'),
            color: METRIC_COLORS.total_liability,
        },
        {
            key: 'total_equity',
            label: t('total_equity'),
            color: METRIC_COLORS.total_equity,
        },
    ]

    // Period comparison filter state
    const [periodSelection, setPeriodSelection] = useState<PeriodSelection>({
        startDate: filters?.gte || dayjs().startOf('year').format('YYYY-MM-DD'),
        endDate: filters?.lte || dayjs().endOf('year').format('YYYY-MM-DD'),
        viewMode: 'quarter',
        selectedPeriods: new Set([
            'quarter_1',
            'quarter_2',
            'quarter_3',
            'quarter_4',
        ]),
        selectedMetrics: new Set(metrics.map((m) => m.key)),
    })

    const params: BalanceSheetMonthlySummaryParams = useMemo(
        () => ({
            from_month: filters?.gte || '',
            to_month: filters?.lte || '',
        }),
        [filters]
    )

    // Fetch data
    const { data, renderState, refetch } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.REPORTS_BI_BALANCE_SHEET_MONTHLY, params],
        queryFn: () => getBalanceSheetMonthlySummary(params),
        staleTime: 5 * 60 * 1000,
        placeholderData: true,
    })

    // Modal handlers
    const handleCreate = () => {
        setModalMode('create')
        setEditData(null)
        setModalOpen(true)
    }

    const handleCloseModal = () => {
        setModalOpen(false)
        setEditData(null)
    }

    // Visible metrics state
    const [visibleMetrics, setVisibleMetrics] = useState<
        Set<BalanceSheetMetricKey>
    >(new Set(metrics.map((m) => m.key)))

    // Auto-load cached AI report when data changes
    useEffect(() => {
        if (!data || data.length === 0) return

        const dataHash = createDataHash(data)
        const cacheKey = generateCacheKey({
            type: 'balance_sheet',
            period_range: `${filters?.gte || 'N/A'} đến ${filters?.lte || 'N/A'}`,
            selected_metrics: Array.from(visibleMetrics),
            data_hash: dataHash,
        })

        const cachedReport = getCachedAnalysis(cacheKey)
        if (cachedReport) {
            setAiReport(cachedReport)
            setAiFromCache(true)
            setShowAiReport(true)
            setAiError(null)
        }
    }, [data, filters?.gte, filters?.lte, visibleMetrics])

    // Toggle metric visibility
    const toggleMetric = (key: BalanceSheetMetricKey) => {
        setVisibleMetrics((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(key)) {
                newSet.delete(key)
            } else {
                newSet.add(key)
            }
            return newSet
        })
    }

    // Toggle all metrics
    const toggleAll = () => {
        if (visibleMetrics.size === metrics.length) {
            setVisibleMetrics(new Set())
        } else {
            setVisibleMetrics(new Set(metrics.map((m) => m.key)))
        }
    }

    // Prepare chart data with filled missing months
    const chartData = useMemo(() => {
        if (!data || !filters?.gte || !filters?.lte) return []

        // Generate all months in the range
        const allMonths: string[] = []
        const start = dayjs(filters.gte)
        const end = dayjs(filters.lte)
        let current = start

        while (current.isBefore(end) || current.isSame(end, 'month')) {
            allMonths.push(current.format('YYYY-MM'))
            current = current.add(1, 'month')
        }

        // Create a map of existing data
        const dataMap = new Map(data.map((item) => [item.report_month, item]))

        // Fill all months with data or zeros
        return allMonths.map((month) => {
            const existingData = dataMap.get(month)
            return {
                month,
                total_assets: existingData?.total_assets || 0,
                total_liability: existingData?.total_liability || 0,
                total_equity: existingData?.total_equity || 0,
                is_balanced: existingData?.is_balanced || false,
                variance: existingData?.variance || 0,
            }
        })
    }, [data, filters?.gte, filters?.lte])

    // Format number
    const formatNumber = (num: number) => {
        return new Intl.NumberFormat('vi-VN', {
            maximumFractionDigits: 0,
        }).format(num)
    }

    // Format month
    const formatMonth = (monthStr: string | number | undefined) => {
        if (!monthStr) return ''
        const str = String(monthStr)
        const [year, month] = str.split('-')
        const monthNames = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
        ]
        return `${monthNames[parseInt(month) - 1]} ${year}`
    }

    // Handle AI Analysis
    const handleAIAnalysis = async () => {
        if (!data || data.length === 0) {
            setAiError('Không có dữ liệu để phân tích')
            setShowAiReport(true)
            return
        }

        setShowAiReport(true)
        setAiLoading(true)
        setAiError(null)
        setAiReport(null)
        setAiFromCache(false)

        try {
            const dataHash = createDataHash(data)
            const cacheKey = generateCacheKey({
                type: 'balance_sheet',
                period_range: `${filters?.gte || 'N/A'} đến ${filters?.lte || 'N/A'}`,
                selected_metrics: Array.from(visibleMetrics),
                data_hash: dataHash,
            })

            const cachedReport = getCachedAnalysis(cacheKey)
            if (cachedReport) {
                setAiReport(cachedReport)
                setAiFromCache(true)
                setAiLoading(false)
                return
            }

            const report = await analyzeBalanceSheet({
                data,
                period_range: `${filters?.gte || 'N/A'} đến ${filters?.lte || 'N/A'}`,
                selected_metrics: Array.from(visibleMetrics),
            })
            setAiReport(report)
            setAiFromCache(false)
        } catch (error: any) {
            console.error('AI Analysis Error:', error)
            setAiError(
                error?.message ||
                    'Không thể kết nối với dịch vụ AI. Vui lòng thử lại sau.'
            )
        } finally {
            setAiLoading(false)
        }
    }

    // Prepare comparison data
    const comparisonData = useMemo(() => {
        if (!chartData.length) return { years: [], quarters: [], months: [] }

        // Group data by year, quarter, and month
        const yearlyData: Record<
            string,
            { [key in BalanceSheetMetricKey]: number[] }
        > = {}
        const quarterlyData: Record<
            string,
            { [key in BalanceSheetMetricKey]: number[] }
        > = {}
        const monthlyData: Record<
            string,
            { [key in BalanceSheetMetricKey]: number }
        > = {}

        chartData.forEach((item) => {
            const [year, month] = item.month.split('-')
            const monthNum = parseInt(month)
            const quarter = Math.ceil(monthNum / 3)
            const quarterKey = `Q${quarter} ${year}`
            const monthKey = `T${monthNum} ${year}`

            // Yearly aggregation (average)
            if (!yearlyData[year]) {
                yearlyData[year] = {
                    total_assets: [],
                    total_liability: [],
                    total_equity: [],
                }
            }
            metrics.forEach((metric) => {
                yearlyData[year][metric.key].push(item[metric.key])
            })

            // Quarterly aggregation (average)
            if (!quarterlyData[quarterKey]) {
                quarterlyData[quarterKey] = {
                    total_assets: [],
                    total_liability: [],
                    total_equity: [],
                }
            }
            metrics.forEach((metric) => {
                quarterlyData[quarterKey][metric.key].push(item[metric.key])
            })

            // Monthly data
            monthlyData[monthKey] = {
                total_assets: item.total_assets,
                total_liability: item.total_liability,
                total_equity: item.total_equity,
            }
        })

        // Calculate averages for years and quarters
        const calculateAverage = (arr: number[]) =>
            arr.reduce((sum, val) => sum + val, 0) / arr.length

        return {
            years: Object.keys(yearlyData)
                .sort()
                .map((year) => ({
                    period: year,
                    data: {
                        total_assets: calculateAverage(
                            yearlyData[year].total_assets
                        ),
                        total_liability: calculateAverage(
                            yearlyData[year].total_liability
                        ),
                        total_equity: calculateAverage(
                            yearlyData[year].total_equity
                        ),
                    },
                })),
            quarters: Object.keys(quarterlyData)
                .sort((a, b) => {
                    const [qA, yearA] = a.split(' ')
                    const [qB, yearB] = b.split(' ')
                    if (yearA !== yearB) return yearA.localeCompare(yearB)
                    return qA.localeCompare(qB)
                })
                .map((quarter) => ({
                    period: quarter,
                    data: {
                        total_assets: calculateAverage(
                            quarterlyData[quarter].total_assets
                        ),
                        total_liability: calculateAverage(
                            quarterlyData[quarter].total_liability
                        ),
                        total_equity: calculateAverage(
                            quarterlyData[quarter].total_equity
                        ),
                    },
                })),
            months: Object.keys(monthlyData)
                .sort((a, b) => {
                    const [tA, yearA] = a.split(' ')
                    const [tB, yearB] = b.split(' ')
                    const monthA = parseInt(tA.replace('T', ''))
                    const monthB = parseInt(tB.replace('T', ''))
                    if (yearA !== yearB) return yearA.localeCompare(yearB)
                    return monthA - monthB
                })
                .map((month) => ({
                    period: month,
                    data: monthlyData[month],
                })),
        }
    }, [chartData, metrics])

    // Filter comparison data based on periodSelection
    const filteredComparisonData = useMemo(() => {
        if (periodSelection.viewMode === 'year') {
            return {
                years: comparisonData.years.filter((y) =>
                    periodSelection.selectedPeriods.has(y.period)
                ),
                quarters: [],
                months: [],
            }
        } else if (periodSelection.viewMode === 'quarter') {
            return {
                years: [],
                quarters: comparisonData.quarters.filter((q) => {
                    const quarter = q.period.split(' ')[0] // Get 'Q1', 'Q2', etc
                    return periodSelection.selectedPeriods.has(quarter)
                }),
                months: [],
            }
        } else {
            // month mode
            return {
                years: [],
                quarters: [],
                months: comparisonData.months.filter((m) => {
                    const monthNum = m.period.split(' ')[0].replace('T', '')
                    return periodSelection.selectedPeriods.has(monthNum)
                }),
            }
        }
    }, [comparisonData, periodSelection])

    // Prepare chart data for comparison
    const comparisonChartData = useMemo(() => {
        let periods: {
            period: string
            data: { [key in BalanceSheetMetricKey]: number }
        }[] = []

        if (periodSelection.viewMode === 'year') {
            periods = filteredComparisonData.years
        } else if (periodSelection.viewMode === 'quarter') {
            periods = filteredComparisonData.quarters.slice(-8)
        } else {
            periods = filteredComparisonData.months.slice(-12)
        }

        return periods.map((p) => {
            const item: any = { period: p.period }
            metrics.forEach((metric) => {
                if (periodSelection.selectedMetrics.has(metric.key)) {
                    item[metric.key] = p.data[metric.key]
                }
            })
            return item
        })
    }, [filteredComparisonData, periodSelection, metrics])

    // Calculate growth percentage
    const calculateGrowth = (current: number, previous: number) => {
        if (previous === 0) return 0
        return ((current - previous) / Math.abs(previous)) * 100
    }

    // Format percentage
    const formatPercentage = (value: number) => {
        const formatted = value.toFixed(1)
        return value > 0 ? `+${formatted}%` : `${formatted}%`
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="space-y-2">
            {/* Header */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-end mb-2">
                    <div className="flex gap-2">
                        <Button
                            size="small"
                            color="primary"
                            onClick={handleAIAnalysis}
                            className="flex items-center gap-2"
                            disabled={!data || data.length === 0}
                        >
                            <Sparkles className="w-4 h-4" />
                            {t('analyze_with_ai')}
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcons />}
                            onClick={handleCreate}
                        >
                            {t('create')}
                        </Button>
                        <IconButton
                            size="small"
                            color={viewMode === 'chart' ? 'primary' : 'default'}
                            onClick={() => setViewMode('chart')}
                            className="border text-primary"
                        >
                            <ChartIcons />
                        </IconButton>
                        <IconButton
                            size="small"
                            color={viewMode === 'table' ? 'primary' : 'default'}
                            onClick={() => setViewMode('table')}
                            className="border text-primary"
                        >
                            <GridIcons />
                        </IconButton>
                    </div>
                </div>

                {/* Metric Filters */}
                <div className="flex flex-wrap gap-4 items-center">
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="select-all"
                            checked={visibleMetrics.size === metrics.length}
                            onCheckedChange={toggleAll}
                        />
                        <Label
                            htmlFor="select-all"
                            className="text-sm cursor-pointer"
                        >
                            {t('select_all')}
                        </Label>
                    </div>

                    {metrics.map((metric) => (
                        <div
                            key={metric.key}
                            className="flex items-center gap-2"
                        >
                            <Checkbox
                                id={metric.key}
                                checked={visibleMetrics.has(metric.key)}
                                onCheckedChange={() => toggleMetric(metric.key)}
                                style={
                                    visibleMetrics.has(metric.key)
                                        ? {
                                              backgroundColor: metric.color,
                                              borderColor: metric.color,
                                          }
                                        : { borderColor: metric.color }
                                }
                            />
                            <Label
                                htmlFor={metric.key}
                                className="text-sm cursor-pointer"
                                style={{
                                    color: visibleMetrics.has(metric.key)
                                        ? metric.color
                                        : undefined,
                                }}
                            >
                                {metric.label}
                            </Label>
                        </div>
                    ))}
                </div>
            </div>

            {/* Chart View - Stacked Bar Chart */}
            {viewMode === 'chart' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <Typogaphy className="font-medium text-base mb-4 text-primary">
                        {t('balance_sheet_composition')}
                    </Typogaphy>
                    <ResponsiveContainer width="100%" height={500}>
                        <ComposedChart
                            data={chartData}
                            margin={{
                                top: 20,
                                right: 30,
                                left: 20,
                                bottom: 5,
                            }}
                        >
                            <CartesianGrid
                                stroke={CHART_THEME.grid.stroke}
                                strokeDasharray={
                                    CHART_THEME.grid.strokeDasharray
                                }
                            />
                            <XAxis
                                dataKey="month"
                                tickFormatter={formatMonth}
                                tick={{
                                    fontSize: CHART_THEME.axis.fontSize,
                                    fontFamily: CHART_THEME.axis.fontFamily,
                                }}
                                stroke={CHART_THEME.axis.stroke}
                            />
                            <YAxis
                                tickFormatter={(value) =>
                                    new Intl.NumberFormat('vi-VN', {
                                        notation: 'compact',
                                        compactDisplay: 'short',
                                    }).format(value)
                                }
                                tick={{
                                    fontSize: CHART_THEME.axis.fontSize,
                                    fontFamily: CHART_THEME.axis.fontFamily,
                                }}
                                stroke={CHART_THEME.axis.stroke}
                            />
                            <Tooltip
                                contentStyle={CHART_THEME.tooltip.contentStyle}
                                itemStyle={CHART_THEME.tooltip.itemStyle}
                                labelStyle={CHART_THEME.tooltip.labelStyle}
                                formatter={(value: number | undefined) =>
                                    value !== undefined
                                        ? formatNumber(value)
                                        : ''
                                }
                                labelFormatter={formatMonth}
                            />
                            <Legend
                                wrapperStyle={{
                                    fontSize: CHART_THEME.legend.fontSize,
                                    fontFamily: CHART_THEME.legend.fontFamily,
                                }}
                            />
                            {visibleMetrics.has('total_liability') && (
                                <Bar
                                    dataKey="total_liability"
                                    name={t('total_liability')}
                                    stackId="balance"
                                    fill={METRIC_COLORS.total_liability}
                                />
                            )}
                            {visibleMetrics.has('total_equity') && (
                                <Bar
                                    dataKey="total_equity"
                                    name={t('total_equity')}
                                    stackId="balance"
                                    fill={METRIC_COLORS.total_equity}
                                />
                            )}
                            {visibleMetrics.has('total_assets') && (
                                <Line
                                    type="monotone"
                                    dataKey="total_assets"
                                    name={t('total_assets')}
                                    stroke={METRIC_COLORS.total_assets}
                                    strokeWidth={2}
                                    dot={{
                                        r: 4,
                                        fill: METRIC_COLORS.total_assets,
                                    }}
                                    activeDot={{ r: 6 }}
                                />
                            )}
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Table View */}
            {viewMode === 'table' && (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <GokuDataGrid
                        apiCall={async () => ({
                            results: data || [],
                            total: data?.length || 0,
                        })}
                        columns={columns(t)}
                        dataGridProps={{
                            getRowId: (row) => row.report_month,
                            hideFooter: true,
                            hideFooterPagination: true,
                        }}
                        options={{
                            pageSize: 50,
                            showSearch: false,
                            title: undefined,
                        }}
                        reactQueryKey={
                            `${QUERY_KEYS.REPORTS_BI_BALANCE_SHEET_MONTHLY}` +
                            JSON.stringify(params)
                        }
                    />
                </div>
            )}
            <RatioFormModal
                open={modalOpen}
                onClose={handleCloseModal}
                mode={modalMode}
                editData={editData}
                refetch={refetch}
            />

            {/* AI Analysis Report */}
            {showAiReport && (
                <AIAnalysisReport
                    report={aiReport}
                    loading={aiLoading}
                    error={aiError}
                    fromCache={aiFromCache}
                />
            )}

            {/* Period Comparison Filter */}
            <PeriodComparisonFilter
                metrics={metrics}
                onChange={setPeriodSelection}
                defaultStartDate={
                    filters?.gte || dayjs().startOf('year').format('YYYY-MM-DD')
                }
                defaultEndDate={
                    filters?.lte || dayjs().endOf('year').format('YYYY-MM-DD')
                }
            />

            <div className="bg-white border border-gray-200 rounded-lg p-6">
                {/* Header with view toggle */}
                <div className="flex items-center justify-end mb-2">
                    <div className="flex gap-2">
                        <IconButton
                            size="small"
                            color={
                                comparisonViewMode === 'chart'
                                    ? 'primary'
                                    : 'default'
                            }
                            onClick={() => setComparisonViewMode('chart')}
                            className="border"
                        >
                            <ChartIcons />
                        </IconButton>
                        <IconButton
                            size="small"
                            color={
                                comparisonViewMode === 'table'
                                    ? 'primary'
                                    : 'default'
                            }
                            onClick={() => setComparisonViewMode('table')}
                            className="border"
                        >
                            <GridIcons />
                        </IconButton>
                    </div>
                </div>

                {/* Chart View */}
                {comparisonViewMode === 'chart' &&
                    comparisonChartData.length > 0 && (
                        <ResponsiveContainer width="100%" height={500}>
                            <BarChart
                                data={comparisonChartData}
                                margin={{
                                    top: 20,
                                    right: 30,
                                    left: 20,
                                    bottom: 5,
                                }}
                            >
                                <CartesianGrid
                                    stroke={CHART_THEME.grid.stroke}
                                    strokeDasharray={
                                        CHART_THEME.grid.strokeDasharray
                                    }
                                />
                                <XAxis
                                    dataKey="period"
                                    tick={{
                                        fontSize: CHART_THEME.axis.fontSize,
                                        fontFamily: CHART_THEME.axis.fontFamily,
                                    }}
                                    stroke={CHART_THEME.axis.stroke}
                                />
                                <YAxis
                                    tickFormatter={(value) =>
                                        new Intl.NumberFormat('vi-VN', {
                                            notation: 'compact',
                                            compactDisplay: 'short',
                                        }).format(value)
                                    }
                                    tick={{
                                        fontSize: CHART_THEME.axis.fontSize,
                                        fontFamily: CHART_THEME.axis.fontFamily,
                                    }}
                                    stroke={CHART_THEME.axis.stroke}
                                />
                                <Tooltip
                                    contentStyle={
                                        CHART_THEME.tooltip.contentStyle
                                    }
                                    itemStyle={CHART_THEME.tooltip.itemStyle}
                                    labelStyle={CHART_THEME.tooltip.labelStyle}
                                    formatter={(value: number | undefined) =>
                                        value !== undefined
                                            ? formatNumber(value)
                                            : ''
                                    }
                                />
                                <Legend
                                    wrapperStyle={{
                                        fontSize: CHART_THEME.legend.fontSize,
                                        fontFamily:
                                            CHART_THEME.legend.fontFamily,
                                    }}
                                />

                                {metrics
                                    .filter((m) =>
                                        periodSelection.selectedMetrics.has(
                                            m.key
                                        )
                                    )
                                    .map((metric) => (
                                        <Bar
                                            key={metric.key}
                                            dataKey={metric.key}
                                            name={metric.label}
                                            fill={metric.color}
                                        />
                                    ))}
                            </BarChart>
                        </ResponsiveContainer>
                    )}

                {/* Table View */}
                {comparisonViewMode === 'table' && (
                    <>
                        {/* Year Comparison */}
                        {filteredComparisonData.years.length > 0 && (
                            <div className="mb-6">
                                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                                    {t('year_over_year')}
                                </h4>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-gray-200">
                                                <th className="text-left py-2 px-3 font-semibold text-gray-600">
                                                    {t('metric')}
                                                </th>
                                                {filteredComparisonData.years.map(
                                                    (year, idx) => (
                                                        <th
                                                            key={year.period}
                                                            className="text-right py-2 px-3 font-semibold text-gray-600"
                                                        >
                                                            {year.period}
                                                            {idx > 0 && (
                                                                <span className="ml-2 text-xs text-gray-500">
                                                                    (vs{' '}
                                                                    {
                                                                        filteredComparisonData
                                                                            .years[
                                                                            idx -
                                                                                1
                                                                        ].period
                                                                    }
                                                                    )
                                                                </span>
                                                            )}
                                                        </th>
                                                    )
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {metrics
                                                .filter((m) =>
                                                    periodSelection.selectedMetrics.has(
                                                        m.key
                                                    )
                                                )
                                                .map((metric) => (
                                                    <tr
                                                        key={metric.key}
                                                        className="border-b border-gray-100"
                                                    >
                                                        <td className="py-2 px-3">
                                                            {metric.label}
                                                        </td>
                                                        {filteredComparisonData.years.map(
                                                            (year, idx) => {
                                                                const value =
                                                                    year.data[
                                                                        metric
                                                                            .key
                                                                    ]
                                                                const prevValue =
                                                                    idx > 0
                                                                        ? filteredComparisonData
                                                                              .years[
                                                                              idx -
                                                                                  1
                                                                          ]
                                                                              .data[
                                                                              metric
                                                                                  .key
                                                                          ]
                                                                        : null
                                                                const growth =
                                                                    prevValue !==
                                                                    null
                                                                        ? calculateGrowth(
                                                                              value,
                                                                              prevValue
                                                                          )
                                                                        : null

                                                                return (
                                                                    <td
                                                                        key={
                                                                            year.period
                                                                        }
                                                                        className="text-right py-2 px-3"
                                                                    >
                                                                        <div>
                                                                            {formatNumber(
                                                                                value
                                                                            )}
                                                                        </div>
                                                                        {growth !==
                                                                            null && (
                                                                            <div
                                                                                className={`text-xs ${
                                                                                    growth >
                                                                                    0
                                                                                        ? 'text-green-600'
                                                                                        : growth <
                                                                                            0
                                                                                          ? 'text-red-600'
                                                                                          : 'text-gray-500'
                                                                                }`}
                                                                            >
                                                                                {formatPercentage(
                                                                                    growth
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                )
                                                            }
                                                        )}
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Quarter Comparison */}
                        {filteredComparisonData.quarters.length > 0 && (
                            <div className="mb-6">
                                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                                    {t('quarter_over_quarter')}
                                </h4>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-gray-200">
                                                <th className="text-left py-2 px-3 font-semibold text-gray-600">
                                                    {t('metric')}
                                                </th>
                                                {filteredComparisonData.quarters
                                                    .slice(-8)
                                                    .map(
                                                        (quarter, idx, arr) => (
                                                            <th
                                                                key={
                                                                    quarter.period
                                                                }
                                                                className="text-right py-2 px-3 font-semibold text-gray-600 whitespace-nowrap"
                                                            >
                                                                {quarter.period}
                                                                {idx > 0 && (
                                                                    <span className="ml-1 text-xs text-gray-500">
                                                                        (vs{' '}
                                                                        {
                                                                            arr[
                                                                                idx -
                                                                                    1
                                                                            ]
                                                                                .period
                                                                        }
                                                                        )
                                                                    </span>
                                                                )}
                                                            </th>
                                                        )
                                                    )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {metrics
                                                .filter((m) =>
                                                    periodSelection.selectedMetrics.has(
                                                        m.key
                                                    )
                                                )
                                                .map((metric) => (
                                                    <tr
                                                        key={metric.key}
                                                        className="border-b border-gray-100"
                                                    >
                                                        <td className="py-2 px-3">
                                                            {metric.label}
                                                        </td>
                                                        {filteredComparisonData.quarters
                                                            .slice(-8)
                                                            .map(
                                                                (
                                                                    quarter,
                                                                    idx,
                                                                    arr
                                                                ) => {
                                                                    const value =
                                                                        quarter
                                                                            .data[
                                                                            metric
                                                                                .key
                                                                        ]
                                                                    const prevValue =
                                                                        idx > 0
                                                                            ? arr[
                                                                                  idx -
                                                                                      1
                                                                              ]
                                                                                  .data[
                                                                                  metric
                                                                                      .key
                                                                              ]
                                                                            : null
                                                                    const growth =
                                                                        prevValue !==
                                                                        null
                                                                            ? calculateGrowth(
                                                                                  value,
                                                                                  prevValue
                                                                              )
                                                                            : null

                                                                    return (
                                                                        <td
                                                                            key={
                                                                                quarter.period
                                                                            }
                                                                            className="text-right py-2 px-3"
                                                                        >
                                                                            <div>
                                                                                {formatNumber(
                                                                                    value
                                                                                )}
                                                                            </div>
                                                                            {growth !==
                                                                                null && (
                                                                                <div
                                                                                    className={`text-xs ${
                                                                                        growth >
                                                                                        0
                                                                                            ? 'text-green-600'
                                                                                            : growth <
                                                                                                0
                                                                                              ? 'text-red-600'
                                                                                              : 'text-gray-500'
                                                                                    }`}
                                                                                >
                                                                                    {formatPercentage(
                                                                                        growth
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                    )
                                                                }
                                                            )}
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Month Comparison */}
                        {filteredComparisonData.months.length > 0 && (
                            <div>
                                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                                    {t('month_over_month')}
                                </h4>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-gray-200">
                                                <th className="text-left py-2 px-3 font-semibold text-gray-600">
                                                    {t('metric')}
                                                </th>
                                                {filteredComparisonData.months
                                                    .slice(-12)
                                                    .map((month, idx, arr) => (
                                                        <th
                                                            key={month.period}
                                                            className="text-right py-2 px-3 font-semibold text-gray-600 whitespace-nowrap"
                                                        >
                                                            {month.period}
                                                            {idx > 0 && (
                                                                <span className="ml-1 text-xs text-gray-500">
                                                                    (vs{' '}
                                                                    {
                                                                        arr[
                                                                            idx -
                                                                                1
                                                                        ].period
                                                                    }
                                                                    )
                                                                </span>
                                                            )}
                                                        </th>
                                                    ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {metrics
                                                .filter((m) =>
                                                    periodSelection.selectedMetrics.has(
                                                        m.key
                                                    )
                                                )
                                                .map((metric) => (
                                                    <tr
                                                        key={metric.key}
                                                        className="border-b border-gray-100"
                                                    >
                                                        <td className="py-2 px-3">
                                                            {metric.label}
                                                        </td>
                                                        {filteredComparisonData.months
                                                            .slice(-12)
                                                            .map(
                                                                (
                                                                    month,
                                                                    idx,
                                                                    arr
                                                                ) => {
                                                                    const value =
                                                                        month
                                                                            .data[
                                                                            metric
                                                                                .key
                                                                        ]
                                                                    const prevValue =
                                                                        idx > 0
                                                                            ? arr[
                                                                                  idx -
                                                                                      1
                                                                              ]
                                                                                  .data[
                                                                                  metric
                                                                                      .key
                                                                              ]
                                                                            : null
                                                                    const growth =
                                                                        prevValue !==
                                                                        null
                                                                            ? calculateGrowth(
                                                                                  value,
                                                                                  prevValue
                                                                              )
                                                                            : null

                                                                    return (
                                                                        <td
                                                                            key={
                                                                                month.period
                                                                            }
                                                                            className="text-right py-2 px-3"
                                                                        >
                                                                            <div>
                                                                                {formatNumber(
                                                                                    value
                                                                                )}
                                                                            </div>
                                                                            {growth !==
                                                                                null && (
                                                                                <div
                                                                                    className={`text-xs ${
                                                                                        growth >
                                                                                        0
                                                                                            ? 'text-green-600'
                                                                                            : growth <
                                                                                                0
                                                                                              ? 'text-red-600'
                                                                                              : 'text-gray-500'
                                                                                    }`}
                                                                                >
                                                                                    {formatPercentage(
                                                                                        growth
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                    )
                                                                }
                                                            )}
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

export default BalanceSheetTab
